import { useState, useEffect, useMemo, useContext } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";

import dayjs from "dayjs";

import { Flex, Spin, Typography, App, Button } from "antd";

import Breadcrumbs from "@/components/ui/Breadcrumbs";
import MyCalendar from "@/components/MyCalendar";
import CustomSelect from "@/components/inputs/CustomSelect";

import { UserContext } from "@/context/UserContext";

// import PlusIcon from "@/icons/PlusIcon";
import { getPeriodTasks } from "@/api/calendar";
import { getRecurringItems as getRecurringTasks } from "@/api/tasks";
import { getUnitsTree, getItem as getUnit } from "@/api/units";

import routes from "@/routes";

const formatLocal = (d) => d.format("YYYY-MM-DD[T]HH:mm:ss");

const LOCAL_STORAGE_KEY = "calendar";

const VIEW_OPTIONS = [
  { label: "Месяц", value: "month" },
  { label: "Неделя", value: "week" },
  { label: "День", value: "day" },
];

const { Title } = Typography;

// Получаем начальную и конечную дату для текущего дня, недели или месяца
const getStartEndDay = (view) => {
  const now = dayjs();

  switch (view) {
    case "day":
      return {
        startDate: formatLocal(now.startOf("day")), // ...T00:00:00
        endDate: formatLocal(now.endOf("day")), // ...T23:59:59
      };
    case "week": {
      return {
        startDate: formatLocal(now.startOf("week")), // ...T00:00:00
        endDate: formatLocal(now.endOf("week")), // ...T23:59:59
      };
    }
    case "month": {
      return {
        startDate: formatLocal(now.startOf("month")), // 1-е число ...T00:00:00
        endDate: formatLocal(now.endOf("month")), // последнее ...T23:59:59
      };
    }
    default:
      return { startDate: "", endDate: "" };
  }
};

const getDayBounds = (d) => ({
  startDate: formatLocal(d.startOf("day")),
  endDate: formatLocal(d.endOf("day")),
});

const getWeekBounds = (d) => ({
  startDate: formatLocal(d.startOf("week")),
  endDate: formatLocal(d.endOf("week")),
});

const getMonthBounds = (d) => ({
  startDate: formatLocal(d.startOf("month")),
  endDate: formatLocal(d.endOf("month")),
});

const getRangeAfterViewChange = (
  currentView,
  newView,
  currentStartISO,
  currentEndISO
) => {
  const start = dayjs(currentStartISO);
  // const end = dayjs(currentEndISO);

  // Увеличение периода
  if (currentView === "day" && newView === "week") {
    return getWeekBounds(start);
  }
  if (currentView === "day" && newView === "month") {
    return getMonthBounds(start);
  }
  if (currentView === "week" && newView === "month") {
    const anchor = start;
    return getMonthBounds(anchor);
  }

  // Уменьшение периода
  if (currentView === "month" && newView === "week") {
    const firstDayOfMonth = start.startOf("month");
    return getWeekBounds(firstDayOfMonth);
  }
  if (currentView === "month" && newView === "day") {
    const firstDayOfMonth = start.startOf("month");
    return getDayBounds(firstDayOfMonth);
  }
  if (currentView === "week" && newView === "day") {
    const firstDayOfWeek = start.startOf("week");
    return getDayBounds(firstDayOfWeek);
  }

  return { startDate: currentStartISO, endDate: currentEndISO };
};

const Calendar = () => {
  const { unit_uuid } = useParams();
  const [searchParams] = useSearchParams();

  // Получение конкретного query параметра
  const view = searchParams.get("view");
  const startDate = searchParams.get("start");
  const endDate = searchParams.get("end");

  const { message } = App.useApp();
  const { user } = useContext(UserContext);
  const navigate = useNavigate();

  const [unitData, setUnitData] = useState(null);
  const [data, setData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [optionsPending, setOptionsPending] = useState(true);

  const [unitsTree, setUnitsTree] = useState([]);

  const defaultMonth = getMonthBounds(dayjs());

  const initialFilters = {
    startDate: startDate || defaultMonth.startDate,
    endDate: endDate || defaultMonth.endDate,
    view: view || "month",
    type_filter: "all",
    task_type_uuid: null,
  };

  const [filters, setFilters] = useState(initialFilters);

  // Возвращает плоский список РГ
  const getFlatUnits = useMemo(() => {
    const flat = [];

    function traverse(nodes) {
      nodes.forEach((node) => {
        flat.push(node);
        if (Array.isArray(node.children) && node.children.length > 0) {
          traverse(node.children);
        }
      });
    }

    if (!unitsTree) return [];

    traverse(unitsTree);
    return flat;
  }, [unitsTree]);

  // Проверяем, входит ли сегодняшняя дата в текущий диапазон
  const isTodayInRange = useMemo(() => {
    const todayRange = getStartEndDay(filters.view);

    return (
      dayjs(filters.startDate).isSame(dayjs(todayRange.startDate), "minute") &&
      dayjs(filters.endDate).isSame(dayjs(todayRange.endDate), "minute")
    );
  }, [filters.startDate, filters.endDate, filters.view]);

  useEffect(() => {
    fetchOptions();
    // eslint-disable-next-line
  }, [unit_uuid]);

  const fetchOptions = async () => {
    setOptionsPending(true);
    try {
      const [tree, unitData] = await Promise.all([
        unit_uuid
          ? getUnitsTree({ unit_uuid: unit_uuid, from_unit: true })
          : getUnitsTree({ from_unit: true }),
        unit_uuid && getUnit(unit_uuid),
      ]);
      setUnitsTree(tree);
      setUnitData(unitData);

      const localParams =
        JSON.parse(
          localStorage.getItem(
            `${user.user_uuid}_${LOCAL_STORAGE_KEY}_${unit_uuid || ""}`
          )
        ) || initialFilters;

      if (startDate && endDate && view) {
        localParams.startDate = startDate;
        localParams.endDate = endDate;
        localParams.view = view;

        updateFilters(localParams);
      } else {
        setFilters(localParams);
        fetchData(localParams);
      }
    } catch (error) {
      message.open({
        type: "error",
        content: error.message,
        duration: 10,
      });
    } finally {
      setOptionsPending(false);
    }
  };

  const fetchData = async (currentFilters) => {
    setIsLoading(true);

    const params = {
      start_date: currentFilters.startDate,
      end_date: currentFilters.endDate,
      type_filter: currentFilters.type_filter,
      task_type_uuid: currentFilters.task_type_uuid,
      units_uuids: currentFilters.units_uuids?.map((item) => item.value),
      ...(unit_uuid && { unit_uuid }),
    };

    try {
      const [tasks, recurringTasks] = await Promise.all([
        getPeriodTasks(params),
        getRecurringTasks(params),
      ]);

      setData([...(tasks || []), ...(recurringTasks || [])]);
    } catch (error) {
      message.open({
        type: "error",
        content: error.message,
        duration: 10,
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Устанавливаем параметры таблицы и сохраняем их в localStorage
  const setParams = (params) => {
    setFilters(params);

    // Сохраняем параметры пагинации в localStorage
    localStorage.setItem(
      `${user.user_uuid}_${LOCAL_STORAGE_KEY}_${unit_uuid || ""}`,
      JSON.stringify(params)
    );

    navigate(
      routes.calendar({
        view: params.view,
        start: params.startDate,
        end: params.endDate,
      }),
      {
        replace: true,
      }
    );
  };

  const updateFilters = (newFilters) => {
    const updatedFilters = {
      ...filters,
      ...newFilters,
    };

    setParams(updatedFilters);
    fetchData(updatedFilters);
  };

  const handleFilterChange = (newFilters) => {
    updateFilters({
      ...newFilters,
      ...(newFilters.units_uuids && {
        type_filter: "all",
      }),
      ...(newFilters.type_filter && {
        units_uuids:
          newFilters.type_filter === "all"
            ? []
            : newFilters.type_filter === "root"
            ? getFlatUnits.filter(({ readOnly }) => !readOnly)
            : getFlatUnits.filter(({ readOnly }) => readOnly),
      }),
    });
  };

  const handleViewChange = (newView) => {
    const { view: currentView, startDate, endDate } = filters;
    const nextRange = getRangeAfterViewChange(
      currentView,
      newView,
      startDate,
      endDate
    );

    handleFilterChange({
      view: newView,
      ...nextRange,
    });
  };

  const returnToday = () => {
    const today = dayjs();
    const next =
      filters.view === "month"
        ? getMonthBounds(today)
        : filters.view === "week"
        ? getWeekBounds(today)
        : getDayBounds(today);

    updateFilters(next);
  };

  return (
    <>
      {optionsPending ? (
        <div className="spin">
          <Spin size="large" />
        </div>
      ) : (
        <>
          <Flex vertical gap={25}>
            {/* Хлебные крошки и заголовок */}
            <Flex vertical gap={10}>
              <Breadcrumbs
                items={[
                  {
                    title: "Главная",
                    href: routes.dashboards,
                  },

                  unitData?.name
                    ? {
                        title: unitData.name,
                      }
                    : {},
                  {
                    title: "Календарь",
                  },
                ]}
              />

              <Flex align="center" justify="space-between" gap={20}>
                <Title level={2} className="no-margin">
                  Календарь
                </Title>

                <Flex gap={10} style={{ marginRight: 20 }}>
                  {!isTodayInRange && (
                    <Button onClick={returnToday}>Сегодня</Button>
                  )}
                  <CustomSelect
                    popupMatchSelectWidth={false}
                    placement="bottomRight"
                    value={filters.view}
                    options={VIEW_OPTIONS}
                    onChange={handleViewChange}
                  />
                </Flex>
              </Flex>
            </Flex>

            <MyCalendar
              data={data}
              unitsTree={unitsTree}
              isLoading={isLoading}
              filters={filters}
              updateFilters={handleFilterChange}
            />
          </Flex>
        </>
      )}
    </>
  );
};

export default Calendar;
